/**
 * WebGL background rendering — nebula fullscreen quad and breakout tile grid.
 * Parallel to render/canvas2d/background.js.
 */

import { getAllLevels } from '../../levels.js';
import { breakoutTileRgb } from '../../render_utils.js';
import {
    Mesh, Geometry, Shader, GlProgram, UniformGroup, Buffer, RenderTexture, Sprite,
} from './pixi.js';
import { NEBULA_VERT, NEBULA_FRAG } from './shaders.js';

/**
 * Build the nebula background mesh, RT, and fullscreen sprite.
 * Sets wr._nebulaMesh, wr._nebulaUniforms, wr._nebulaRT, wr._nebulaSprite.
 * Called once from WebGLRenderer._initPixi() after Pixi is ready.
 * @param {object} wr  WebGLRenderer instance
 */
export function buildNebulaBackground(wr) {
    const rtW = Math.max(wr._glCanvas.width,  2);
    const rtH = Math.max(wr._glCanvas.height, 2);

    const verts  = new Float32Array([-1,-1, 1,-1, 1,1, -1,1]);
    const idx    = new Uint16Array([0,1,2, 0,2,3]);
    const posBuf = new Buffer({ data: verts, usage: 32|1024 });

    const resData = new Float32Array([rtW, rtH]);
    wr._nebulaUniforms = new UniformGroup({
        uTime:       { value: 0.0,     type: 'f32'       },
        uResolution: { value: resData, type: 'vec2<f32>' },
    });

    const prog   = new GlProgram({ vertex: NEBULA_VERT, fragment: NEBULA_FRAG, name: 'nebula-bg' });
    const shader = new Shader({ glProgram: prog, resources: { uniforms: wr._nebulaUniforms } });
    const geo    = new Geometry({
        attributes: { aPosition: { buffer: posBuf, format: 'float32x2', stride: 8, offset: 0 } },
        indexBuffer: idx,
    });

    wr._nebulaMesh   = new Mesh({ geometry: geo, shader });
    wr._nebulaRT     = RenderTexture.create({ width: rtW, height: rtH, resolution: 1 });
    wr._nebulaSprite = new Sprite(wr._nebulaRT);
    wr._nebulaSprite.width  = wr._glCanvas.width;
    wr._nebulaSprite.height = wr._glCanvas.height;
}

/**
 * Render the nebula shader into wr._nebulaRT (static — called on level change or resize).
 * @param {object} wr
 */
export function renderNebulaRT(wr) {
    if (!wr._nebulaRT || !wr._nebulaMesh) return;
    wr._nebulaUniforms.uniforms.uTime          = wr._nebulaTime;
    wr._nebulaUniforms.uniforms.uResolution[0] = wr._nebulaRT.width;
    wr._nebulaUniforms.uniforms.uResolution[1] = wr._nebulaRT.height;
    wr._nebulaUniforms._dirtyId++;
    wr._pixi.renderer.render({ container: wr._nebulaMesh, target: wr._nebulaRT, clear: true });
}

/**
 * Per-frame background render — updates nebula time offset on level change,
 * keeps the fullscreen sprite sized to the canvas, and draws breakout tiles.
 * @param {object} wr
 * @param {number} dt
 */
export function renderBackground(wr, dt) {
    if (!wr._pixi) {
        wr._inner.renderBackground(dt);
        return;
    }

    const levelId = wr._inner.game?.levelId ?? null;
    if (levelId !== wr._nebulaLevelId) {
        wr._nebulaLevelId = levelId;
        const MINUTES_PER_LEVEL = 5;
        const levels = getAllLevels();
        const idx = levels.findIndex(l => l.id === levelId);
        if (idx >= 0) {
            wr._nebulaTime = idx * MINUTES_PER_LEVEL * 60 * 1.5;
        }
        renderNebulaRT(wr);
    }

    if (wr._nebulaSprite) {
        wr._nebulaSprite.width  = wr._glCanvas.width;
        wr._nebulaSprite.height = wr._glCanvas.height;
    }

    drawBreakoutTiles(wr);
}

/**
 * Redraw the breakout-mode tile grid into wr._breakoutTileGraphics.
 * @param {object} wr
 */
export function drawBreakoutTiles(wr) {
    const tiles = wr._inner.game.getBreakoutTiles?.();
    const tg    = wr._breakoutTileGraphics;
    tg.clear();
    if (!tiles || tiles.length === 0) return;
    const tileSize = wr._inner.game.getBreakoutTileSize?.() ?? 40;
    for (let i = 0; i < tiles.length; i++) {
        const tile = tiles[i];
        const [r, g, b] = breakoutTileRgb(tile);
        const color = (r << 16) | (g << 8) | b;
        tg.rect(tile.x, tile.y, tileSize, tileSize).fill({ color, alpha: 0.85 });
    }
}
